import {Component, PanelLifecycleContext} from 'panel';

import * as colors from 'common/util/colors';
import {assignOrdinalNumbersToSegmentKeys} from 'common/report/util';
import {SavedLegend} from 'common/report/constants';

export interface SegmentItem {
  id: string;
  label: string;
  // support raw color strings to match any color the chart segments use, the chart segments are assumed to use the correct colors
  color?: string;
  // a list of additional segments per section to select when toggled
  relatedSegmentsPerSection?: string[][];
}

export interface SegmentSection {
  label?: string;
  items: SegmentItem[];

  // useful if you expect to getSections() manually to implement custom logic
  metadata?: any;
  /*
  Does this segment section have more data than is shown on the client side?
  */
  apiResultsTruncated?: boolean;
  /*
  Goes along with `apiResultsTruncated`; when the limited segments don't represent the "top" of available data, this suppresses labeling as "12 of top 100"
  */
  suppressShowingTop?: boolean;
}

function getSegmentIdsPerSection(sections: SegmentSection[]): string[][] {
  return sections.map((section) => section.items.map((item) => item.id));
}

function mapToSet(arr: string[][]): Array<Set<string>> {
  return arr.map((subarr) => new Set(subarr));
}

// Note: this manual equality check is significantly faster than calling lodash/isEqual on sections
function sectionSetEquals(sections1: Array<Set<string>>, sections2: Array<Set<string>>): boolean {
  if (sections1?.length !== sections2?.length) {
    return false;
  }
  for (let sectionIdx = 0; sectionIdx < sections1.length; sectionIdx++) {
    const set1 = sections1[sectionIdx];
    const set2 = sections2[sectionIdx];
    if (set1.size !== set2.size) {
      return false;
    }
    for (const item of set1) {
      if (!set2.has(item)) {
        return false;
      }
    }
  }
  return true;
}

type ToggleCallback = () => void;

export class SegmentFilterController implements PanelLifecycleContext {
  private sections: SegmentSection[] | null = null;
  private selectedIdsPerSection: Array<Set<string>> = [];
  private connectedComponents: Set<Component<any>> = new Set();
  private toggleCallbacks: Set<ToggleCallback> = new Set();
  private canApplyDefaults = true;
  private defaultIdsPerSection: string[][] | null = null;
  private assignedColorsPerSection: Array<Map<string, number>> = [];
  private _segmentationQueryLimit: number;
  private _exportSegmentationQueryLimit: number;

  public readonly defaultNumSelections: number;
  public readonly selectAllLimit: number;

  constructor({defaultNumSelections = 6, selectAllLimit = 12} = {}) {
    this.defaultNumSelections = defaultNumSelections;
    this.selectAllLimit = selectAllLimit;
  }

  private updateConnectedComponents(): void {
    for (const component of this.connectedComponents) {
      component.update();
    }
  }
  private invokeToggleCallbacks(): void {
    for (const callback of this.toggleCallbacks) {
      callback();
    }
  }

  /**
   * Clears and replaces all legend items, applies @persistedSelectionsForEachSection once if it changes from a previous value.
   * If supplied with the same value as previously, this method is a noop and does not affect the current selection set
   * @param orderedSections - sections of all available segments
   * @param {string[][]} persistedSelectionsForEachSection - each array element corresponds to a section, and each subarray element is a segment item ID
   */
  public loadSections(orderedSections: SegmentSection[]): void {
    const oldSegmentIdsPerSection = this.sections ? getSegmentIdsPerSection(this.sections) : [];
    const segmentIdsPerSection = getSegmentIdsPerSection(orderedSections);

    // only reset selection if segments have actually changed and not just reordered
    const sectionsChanged = !sectionSetEquals(mapToSet(segmentIdsPerSection), mapToSet(oldSegmentIdsPerSection));
    this.sections = orderedSections;

    if (sectionsChanged) {
      this.selectedIdsPerSection = [];
      this.assignedColorsPerSection = this.sections.map(() => new Map<string, number>());

      if (this.canApplyDefaults && this.defaultIdsPerSection) {
        this.applySelections(this.defaultIdsPerSection);
        this.canApplyDefaults = false;
      } else {
        this.applySelections(this.getTopSegments(), {shouldInvokeToggleCallbacks: false});
      }
    } else {
      this.updateConnectedComponents();
    }
  }

  public getTopSegments() {
    const segmentIdsPerSection = getSegmentIdsPerSection(this.sections);
    return [segmentIdsPerSection[0].slice(0, this.defaultNumSelections), ...segmentIdsPerSection.slice(1)];
  }

  public loadPersistedSelections(defaultIdsPerSection: string[][]) {
    if (this.canApplyDefaults && this.sections) {
      this.applySelections(defaultIdsPerSection, {shouldInvokeToggleCallbacks: false});
      this.canApplyDefaults = false;
    } else {
      this.defaultIdsPerSection = defaultIdsPerSection;
    }
  }

  public loadPersistedColors(assignedColors: Array<Map<string, number>>) {
    if (assignedColors && assignedColors.length) {
      this.assignedColorsPerSection = assignedColors;
      this.updateConnectedComponents();
    }
  }

  /**
   * Loads a saved legend from a bookmark.
   * Note: Does *not* expect it to be reversed, the bookmark should be taken in raw from the bookmark,
   * and this method will deal with reversing it to display the last grouping first.
   * @param savedLegend - Legend from bookmark format
   */
  public loadFromSavedLegend(savedLegend: SavedLegend[]) {
    if (!savedLegend) {
      return;
    }

    const idsPerSection: string[][] = [];
    const colorOverrides: Array<Map<string, number>> = [];

    for (let i = 0; i < savedLegend.length; i++) {
      const legendSection = savedLegend[i];
      const colorsForThisSection = new Map<string, number>();
      const ids: string[] = [];

      for (const item of legendSection.selected) {
        ids.push(item.key);
        // Only the final section should have color, non-final ones have default color 1
        const color = i === savedLegend.length - 1 ? item.color : 1;
        if (color) {
          colorsForThisSection.set(item.key, color);
        }
      }

      idsPerSection.push(ids);
      colorOverrides.push(colorsForThisSection);
    }

    // N.B. We reverse the sections since bookmarks save legends in the groupBy order, but we want to show the most
    // distinguishing legend section first, which is the final breakdown
    this.loadPersistedSelections(idsPerSection.reverse());
    this.loadPersistedColors(colorOverrides.reverse());
  }

  public applySelections(defaultIdsPerSection: string[][], {shouldInvokeToggleCallbacks = true} = {}) {
    const persistedSelections = mapToSet(defaultIdsPerSection);
    this.selectedIdsPerSection = persistedSelections;
    if (shouldInvokeToggleCallbacks) {
      this.invokeToggleCallbacks();
    }
    this.updateConnectedComponents();
  }

  public toggleItem(sectionIdx: number, id: string): void {
    const selectedIds = this.selectedIdsPerSection[sectionIdx];

    if (selectedIds.has(id)) {
      selectedIds.delete(id);
      // remove saved color
      this.assignedColorsPerSection[sectionIdx].delete(id);
    } else {
      selectedIds.add(id);

      // select related segments
      const sectionItem = this.sections[sectionIdx].items.find((segment) => segment.id === id);
      if (sectionItem.relatedSegmentsPerSection) {
        this.selectedIdsPerSection = this.selectedIdsPerSection.map(
          (selectedIdsInSection, sectionIdx) =>
            new Set([...selectedIdsInSection, ...sectionItem.relatedSegmentsPerSection[sectionIdx]]),
        );
      }
    }

    this.invokeToggleCallbacks();
    this.updateConnectedComponents();
  }

  public toggleAllItems(): void {
    // select-all is only concerned with the first section
    const allSelected =
      this.selectedIdsPerSection[0].size === Math.min(this.sections[0].items.length, this.selectAllLimit);

    const idsPerSection = getSegmentIdsPerSection(this.sections);
    const toggledSelection = allSelected
      ? [[], ...idsPerSection.slice(1)]
      : [idsPerSection[0].slice(0, this.selectAllLimit), ...idsPerSection.slice(1)];

    this.selectedIdsPerSection = mapToSet(toggledSelection);

    this.invokeToggleCallbacks();
    this.updateConnectedComponents();
  }

  public hasLoaded(): boolean {
    return this.sections !== null;
  }

  public getSections(): SegmentSection[] {
    return this.sections ?? [];
  }

  public getItems(sectionIdx: number): SegmentItem[] {
    return this.getSections()[sectionIdx]?.items ?? [];
  }

  public getSelectedItems(sectionIdx: number): SegmentItem[] {
    return this.getItems(sectionIdx).filter((item) => this.isItemSelected(sectionIdx, item.id));
  }

  public getSelectedCount(sectionIdx: number): number {
    return this.selectedIdsPerSection[sectionIdx]?.size ?? 0;
  }

  public isItemSelected(sectionIdx: number, id: string): boolean {
    return this.selectedIdsPerSection[sectionIdx]?.has(id) ?? false;
  }

  public set segmentationQueryLimit(limit: number) {
    this._segmentationQueryLimit = limit;
  }

  public get segmentationQueryLimit(): number {
    return this._segmentationQueryLimit;
  }

  public set exportSegmentationQueryLimit(limit: number) {
    this._exportSegmentationQueryLimit = limit;
  }

  public get exportSegmentationQueryLimit(): number {
    return this._exportSegmentationQueryLimit;
  }

  // Get the overridden color, otherwise default to the supplied ones.
  // This allows widgets to opt-in to the new color overrides
  // To opt in, you can just omit `color` in the legend segments when you pass them initially to the component
  public getItemColor(sectionIdx: number, id: string): string {
    // if it's selected, then we have to actually give it a color. If not, we dynamically determine color (unless given)
    if (this.isItemSelected(sectionIdx, id)) {
      const item = this.sections[sectionIdx].items.find((segmentItem) => segmentItem.id === id);

      // only assign colors for first section
      if (sectionIdx > 0 || item?.color) {
        return item?.color ?? null; // null means default checkbox color
      }

      if (this.assignedColorsPerSection[sectionIdx].has(id)) {
        return colors.getChartSegmentColorCombo(this.assignedColorsPerSection[sectionIdx].get(id) - 1).primary;
      }

      // otherwise: assign new colors, and save them
      const colorAssignments = assignOrdinalNumbersToSegmentKeys(
        this.assignedColorsPerSection[sectionIdx],
        Array.from(this.selectedIdsPerSection[sectionIdx].keys()).filter(
          // Make sure we don't re-assign colors that already have them
          (key) => !this.assignedColorsPerSection[sectionIdx].has(key),
        ),
        colors.charts.default.length,
      );
      this.assignedColorsPerSection[sectionIdx] = colorAssignments;

      return colors.getChartSegmentColorCombo(this.assignedColorsPerSection[sectionIdx].get(id) - 1).primary;
    } else {
      // Since we don't know what color this could take, set it to be a default gray color
      return colors.gray1;
    }
  }

  public subscribeToggle(callback: ToggleCallback): void {
    this.toggleCallbacks.add(callback);
  }
  public unsubscribeToggle(callback: ToggleCallback): void {
    this.toggleCallbacks.delete(callback);
  }

  // Panel Context lifecycle methods
  public bindToComponent(component: Component<any, any, any, any, any>): void {
    this.connectedComponents.add(component);
  }
  public unbindFromComponent(component: Component<any, any, any, any, any>): void {
    this.connectedComponents.delete(component);
  }
}
